import React, { useEffect, useRef } from 'react';

import { TrackableProperties } from '@lightspeed-payments/payments-portal-sdk';

import { getMerchantDetails } from './getMerchantDetails';
import { useTracking } from './TrackingContext';

/**
 * Defines the options for configuring a VisibilityTracker instance.
 *
 * @property {React.ReactNode} children - The children elements to be wrapped by the VisibilityTracker.
 * @property {string} id - Unique identifier for the element being tracked. This is used in the tracking event to
 *                         distinguish between different elements.
 * @property {string} eventName - The name of the event to be sent to the analytics service when the element becomes
 *                                visible.
 * @property {() => void} [onVisible] - An optional callback function that is executed when the element becomes visible.
 * @property {number} [threshold=0.5] - An optional threshold for the IntersectionObserver, representing a
 *                                      percentage (0-1) of the element's visibility required to trigger the event.
 *                                      Default is 0.5.
 * @property {boolean} [triggerOnce=false] - An optional flag to indicate whether the event should only be triggered
 *                                           once. Default is false.
 * @property {TrackableProperties} [eventData={}] - Optional additional data to be sent along with
 *                                                  the tracking event. Common identity props
 *                                                  (merchant_name, pli, product_id, surface, id)
 *                                                  cannot be overridden via eventData.
 */
interface VisibilityTrackerProps {
  children: React.ReactNode;
  id: string;
  eventName: string;
  onVisible?: () => void;
  threshold?: number;
  triggerOnce?: boolean;
  eventData?: TrackableProperties;
}

/**
 * `VisibilityTracker` is a React component that uses the Intersection Observer API to track
 * the visibility of its children within the viewport. It triggers a tracking event when the
 * conditions defined by the props are met.
 *
 * @example
 * <VisibilityTracker
 *   id="uniqueId"
 *   eventName="ElementViewed"
 *   onVisible={() => console.log('Element is visible')}
 *   triggerOnce={true}
 * >
 *   <div>Content to track</div>
 * </VisibilityTracker>
 *
 * @returns {ReactElement} A React element wrapping the children with visibility tracking functionality.
 */
export const VisibilityTracker = ({
  children,
  id,
  eventName,
  onVisible,
  threshold = 0.5,
  triggerOnce = false,
  eventData = {},
}: VisibilityTrackerProps) => {
  const { trackEvent } = useTracking();
  const { pli, merchantName } = getMerchantDetails();
  const validThreshold = Math.min(1, Math.max(0, threshold));
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!nodeRef.current) return undefined;

    const handleChange = (entries: IntersectionObserverEntry[]) => {
      const entry = entries.find((e) => e.target === nodeRef.current);
      if (entry?.isIntersecting) {
        trackEvent(eventName, {
          ...eventData,
          merchant_name: merchantName,
          pli,
          product_id: 'r-series',
          surface: 'r-series',
          id,
        });
        onVisible?.();

        if (triggerOnce) {
          observerRef.current?.disconnect();
          observerRef.current = null;
          nodeRef.current = null;
        }
      }
    };

    const observer = new IntersectionObserver(handleChange, {
      threshold: validThreshold,
    });

    observer.observe(nodeRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [
    eventData,
    eventName,
    id,
    merchantName,
    onVisible,
    pli,
    threshold,
    trackEvent,
    triggerOnce,
    validThreshold,
  ]);

  return <div ref={nodeRef}>{children}</div>;
};
